import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics } from '../context/AnalyticsContext';
import { useNotifications } from '../context/NotificationContext';
import {
  useGetCampDateBookings,
  useCreateAdminBooking,
  useDeleteParticipant,
  useRestoreParticipant,
  useUpdateCampDateCapacity,
  CampDateAdmin,
  CampDateBooking,
  AdminParticipant,
} from '../api/organizerAdmin';
import { CampFormatUtils } from '../utils/campFormatUtils';
import {
  AlertCircle,
  ArrowLeft,
  Plus,
  X,
  Calendar,
  Users,
  Mail,
  Phone,
  Trash2,
  RotateCcw,
  CheckCircle,
  Clock,
  XCircle,
  Pencil,
} from 'lucide-react';
import './OrganizerAdminCampBookings.css';

const OrganizerAdminCampBookings: React.FC = () => {
  const { organizerId, campId, dateId } = useParams<{ organizerId: string; campId: string, dateId: string}>();
  const navigate = useNavigate();
  const { trackPageView } = useAnalytics();
  const { showSuccess, showError } = useNotifications();

  // Fetch camp date bookings
  const {
    data: campDateResponse,
    isLoading: isLoadingDates,
    error: datesError,
  } = useGetCampDateBookings(organizerId, campId, dateId);

  // Extract camp and camp date from response
  const camp = campDateResponse?.camp;
  const campDate = campDateResponse?.camp_date;
  const bookings = campDateResponse?.bookings;

  // Track page view
  useEffect(() => {
    if (organizerId && campId && dateId) {
      trackPageView(`/admin/organizer/${organizerId}/camps/${campId}/dates/${dateId}`, 'Camp Bookings');
    }
  }, [organizerId, campId, trackPageView]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate(`/admin/organizer/${organizerId}`);
  };

  const getStatusBadge = (status: string | undefined) => {
    const statusConfig: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
      pending: { icon: <Clock className="icon icon--xs" />, className: 'status-pending', label: 'Pending' },
      confirmed: { icon: <CheckCircle className="icon icon--xs" />, className: 'status-confirmed', label: 'Confirmed' },
      completed: { icon: <CheckCircle className="icon icon--xs" />, className: 'status-completed', label: 'Completed' },
      cancelled: { icon: <XCircle className="icon icon--xs" />, className: 'status-cancelled', label: 'Cancelled' },
      expired: { icon: <AlertCircle className="icon icon--xs" />, className: 'status-expired', label: 'Expired' },
    };

    const config = statusConfig[status || 'pending'] || statusConfig.pending;

    return (
      <span className={`status-badge ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  // Loading state
  if (isLoadingDates) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="camp-bookings-loading">
            <div className="loading-spinner"></div>
            <p>Loading camp details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (datesError) {
    return (
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="camp-bookings-error">
            <AlertCircle className="icon icon--xl" />
            <h2>Camp Not Found</h2>
            <p>Unable to find the requested camp.</p>
            <button className="btn btn--primary" onClick={handleBack}>
              <ArrowLeft className="icon icon--sm" />
              Back to Dashboard
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <div className="camp-bookings-container">
          {/* Header with back button */}
          <div className="camp-bookings-header">
            <button className="btn btn--secondary btn--sm" onClick={handleBack}>
              <ArrowLeft className="icon icon--sm" />
              Back
            </button>

            <div className="camp-bookings-title-section">
              <h1 className="camp-bookings-title">{camp?.name || 'Camp Bookings'}</h1>
              <p className="camp-bookings-subtitle">Manage bookings and participants</p>
            </div>
          </div>

          {/* Camp Date Bookings */}
          {campDate && (
            <CampDateSection
              campDate={campDate}
              bookings={bookings || []}
              organizerId={organizerId!}
              campId={campId!}
              getStatusBadge={getStatusBadge}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Participant form entry
interface ParticipantEntry {
  name: string;
  birthdate: string;
  details: string;
}

const emptyParticipant = (): ParticipantEntry => ({ name: '', birthdate: '', details: '' });

// Camp Date Section Component
interface CampDateSectionProps {
  campDate: CampDateAdmin;
  bookings: CampDateBooking[];
  organizerId: string;
  campId: string;
  getStatusBadge: (status: string | undefined) => React.ReactNode;
  showSuccess: (message: string) => void;
  showError: (message: string, error: Error) => void;
}

const CampDateSection: React.FC<CampDateSectionProps> = ({
  campDate,
  bookings,
  organizerId,
  campId,
  getStatusBadge,
  showSuccess,
  showError,
}) => {
  // Capacity modal state
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityValue, setCapacityValue] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(false);

  // Add booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRelationship, setContactRelationship] = useState('');
  const [participants, setParticipants] = useState<ParticipantEntry[]>([emptyParticipant()]);

  const campDateId = String(campDate.id);

  const updateCapacity = useUpdateCampDateCapacity(organizerId, campId, campDateId);
  const createAdminBooking = useCreateAdminBooking(organizerId, campId, campDateId);

  const totalParticipants = campDate.current_capacity;

  // Capacity modal handlers
  const handleOpenCapacityModal = () => {
    if (campDate.max_capacity === null) {
      setIsUnlimited(true);
      setCapacityValue('');
    } else {
      setIsUnlimited(false);
      setCapacityValue(String(campDate.max_capacity));
    }
    setShowCapacityModal(true);
  };

  const handleCloseCapacityModal = () => {
    setShowCapacityModal(false);
  };

  const handleSaveCapacity = async (e: React.FormEvent) => {
    e.preventDefault();
    const newCapacity = isUnlimited ? null : Number(capacityValue);

    try {
      await updateCapacity.mutateAsync({ max_capacity: newCapacity });
      showSuccess('Capacity updated successfully');
      handleCloseCapacityModal();
    } catch (error) {
      showError('Failed to update capacity', error as Error);
    }
  };

  // Booking modal handlers
  const handleOpenBookingModal = () => {
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setContactRelationship('');
    setParticipants([emptyParticipant()]);
    setShowBookingModal(true);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
  };

  const handleParticipantChange = (index: number, field: keyof ParticipantEntry, value: string) => {
    const updated = [...participants];
    updated[index] = { ...updated[index], [field]: value };
    setParticipants(updated);
  };

  const handleAddParticipantRow = () => {
    setParticipants([...participants, emptyParticipant()]);
  };

  const handleRemoveParticipantRow = (index: number) => {
    if (participants.length <= 1) return;
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const handleCreateBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    const validParticipants = participants.filter(p => p.name.trim());
    if (validParticipants.length === 0) {
      showError('At least one participant name is required', new Error('Validation failed'));
      return;
    }

    try {
      await createAdminBooking.mutateAsync({
        participants: validParticipants.map(p => ({
          name: p.name.trim(),
          birthdate: p.birthdate || undefined,
          details: p.details.trim() || undefined,
        })),
        contact_name: contactName.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        contact_phone: contactPhone.trim() || undefined,
        contact_relationship_to_participant: contactRelationship.trim() || undefined,
      });
      showSuccess('Booking created successfully');
      handleCloseBookingModal();
    } catch (error) {
      showError('Failed to create booking', error as Error);
    }
  };

  return (
    <div className="camp-date-section">
      <div className="camp-date-header">
        <div className="camp-date-info">
          <h3 className="camp-date-title">
            <Calendar className="icon icon--sm" />
            {CampFormatUtils.formatDateRange(campDate.start_date, campDate.end_date, true)}
          </h3>
          <div className="camp-date-meta">
            <span className="camp-date-capacity">
              <Users className="icon icon--xs" />
              {totalParticipants} / {campDate.max_capacity ?? '∞'} participants
            </span>
            <span className="camp-date-bookings">
              {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="camp-date-actions">
          <button className="btn btn--primary btn--sm" onClick={handleOpenBookingModal}>
            <Plus className="icon icon--sm" />
            Add Booking
          </button>
          <button
            className="btn btn--secondary btn--sm"
            onClick={handleOpenCapacityModal}
          >
            <Pencil className="icon icon--xs" />
            Edit Capacity
          </button>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div className="camp-date-empty">
          <p>No bookings for this date yet.</p>
        </div>
      ) : (
        <div className="camp-date-bookings-list">
          {bookings.map((booking) => (
            <BookingRow
              key={booking.uuid}
              booking={booking}
              organizerId={organizerId}
              getStatusBadge={getStatusBadge}
              showSuccess={showSuccess}
              showError={showError}
            />
          ))}
        </div>
      )}

      {/* Edit Capacity Modal */}
      {showCapacityModal && (
        <div className="modal-overlay" onClick={handleCloseCapacityModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Capacity</h2>
              <button className="modal-close-btn" onClick={handleCloseCapacityModal}>
                <X className="icon icon--sm" />
              </button>
            </div>

            <form onSubmit={handleSaveCapacity} className="capacity-form">
              <p className="capacity-info">
                Currently {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''} enrolled
              </p>

              <div className="form-group">
                <label htmlFor={`capacity-${campDate.id}`}>Max Capacity</label>
                <input
                  type="number"
                  id={`capacity-${campDate.id}`}
                  className="form-input"
                  value={capacityValue}
                  onChange={(e) => setCapacityValue(e.target.value)}
                  min={0}
                  placeholder="Enter max capacity"
                  disabled={isUnlimited}
                />
              </div>

              <div className="capacity-unlimited-row">
                <input
                  type="checkbox"
                  id={`unlimited-${campDate.id}`}
                  checked={isUnlimited}
                  onChange={(e) => {
                    setIsUnlimited(e.target.checked);
                    if (e.target.checked) {
                      setCapacityValue('');
                    }
                  }}
                />
                <label htmlFor={`unlimited-${campDate.id}`}>Unlimited capacity</label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleCloseCapacityModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={updateCapacity.isPending || (!isUnlimited && !capacityValue)}
                >
                  {updateCapacity.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Booking Modal */}
      {showBookingModal && (
        <div className="modal-overlay" onClick={handleCloseBookingModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Booking</h2>
              <button className="modal-close-btn" onClick={handleCloseBookingModal}>
                <X className="icon icon--sm" />
              </button>
            </div>

            <form onSubmit={handleCreateBooking} className="admin-booking-form">
              {/* Contact Information */}
              <h3 className="admin-booking-section-title">Contact Information</h3>

              <div className="admin-booking-contact-grid">
                <div className="form-group">
                  <label htmlFor={`contact-name-${campDate.id}`}>Name</label>
                  <input
                    type="text"
                    id={`contact-name-${campDate.id}`}
                    className="form-input"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Contact name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`contact-email-${campDate.id}`}>Email</label>
                  <input
                    type="email"
                    id={`contact-email-${campDate.id}`}
                    className="form-input"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="Contact email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`contact-phone-${campDate.id}`}>Phone</label>
                  <input
                    type="tel"
                    id={`contact-phone-${campDate.id}`}
                    className="form-input"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`contact-relationship-${campDate.id}`}>Relationship</label>
                  <input
                    type="text"
                    id={`contact-relationship-${campDate.id}`}
                    className="form-input"
                    value={contactRelationship}
                    onChange={(e) => setContactRelationship(e.target.value)}
                    placeholder="e.g. Parent, Guardian"
                  />
                </div>
              </div>

              {/* Participants */}
              <h3 className="admin-booking-section-title">Participants</h3>

              {participants.map((participant, index) => (
                <div key={index} className="admin-booking-participant-row">
                  <div className="admin-booking-participant-fields">
                    <div className="form-group">
                      <label htmlFor={`p-name-${campDate.id}-${index}`}>Name *</label>
                      <input
                        type="text"
                        id={`p-name-${campDate.id}-${index}`}
                        className="form-input"
                        value={participant.name}
                        onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                        placeholder="Participant name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`p-birth-${campDate.id}-${index}`}>Birthdate</label>
                      <input
                        type="date"
                        id={`p-birth-${campDate.id}-${index}`}
                        className="form-input"
                        value={participant.birthdate}
                        onChange={(e) => handleParticipantChange(index, 'birthdate', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`p-details-${campDate.id}-${index}`}>Details</label>
                      <input
                        type="text"
                        id={`p-details-${campDate.id}-${index}`}
                        className="form-input"
                        value={participant.details}
                        onChange={(e) => handleParticipantChange(index, 'details', e.target.value)}
                        placeholder="Notes or medical info"
                      />
                    </div>
                  </div>

                  {participants.length > 1 && (
                    <button
                      type="button"
                      className="btn-icon btn-icon--delete"
                      onClick={() => handleRemoveParticipantRow(index)}
                      title="Remove participant"
                    >
                      <Trash2 className="icon icon--xs" />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={handleAddParticipantRow}
              >
                <Plus className="icon icon--sm" />
                Add Another Participant
              </button>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={handleCloseBookingModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={createAdminBooking.isPending}
                >
                  {createAdminBooking.isPending ? 'Creating...' : 'Create Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Booking Row Component - displays all info inline
interface BookingRowProps {
  booking: CampDateBooking;
  organizerId: string;
  getStatusBadge: (status: string | undefined) => React.ReactNode;
  showSuccess: (message: string) => void;
  showError: (message: string, error: Error) => void;
}

const BookingRow: React.FC<BookingRowProps> = ({
  booking,
  organizerId,
  getStatusBadge,
  showSuccess,
  showError,
}) => {
  return (
    <div className="booking-row">
      {/* Contact & Status Info */}
      <div className="booking-row-header">
        <div className="booking-contact">
          <span className="booking-contact-name">{booking.contact_name || 'No contact'}</span>
          {booking.contact_email && (
            <span className="booking-contact-email">
              <Mail className="icon icon--xs" />
              {booking.contact_email}
            </span>
          )}
          {booking.contact_phone && (
            <span className="booking-contact-phone">
              <Phone className="icon icon--xs" />
              {booking.contact_phone}
            </span>
          )}
          {booking.contact_relationship && (
            <span className="booking-contact-relationship">({booking.contact_relationship})</span>
          )}
        </div>
        <div className="booking-row-status">
          {getStatusBadge(booking.status)}
        </div>
      </div>

      {/* Participants List */}
      <div className="booking-participants">
        {booking.participants && booking.participants.length > 0 ? (
          booking.participants.map((participant) => (
            <ParticipantInline
              key={participant.id}
              participant={participant}
              organizerId={organizerId}
              bookingUuid={booking.uuid}
              onSuccess={() => showSuccess('Participant updated')}
              onError={(msg) => showError(msg, new Error(msg))}
            />
          ))
        ) : (
          <span className="no-participants">No participants</span>
        )}
      </div>
    </div>
  );
};

// Participant Inline Component
interface ParticipantInlineProps {
  participant: AdminParticipant;
  organizerId: string;
  bookingUuid: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const ParticipantInline: React.FC<ParticipantInlineProps> = ({
  participant,
  organizerId,
  bookingUuid,
  onSuccess,
  onError,
}) => {
  const deleteParticipant = useDeleteParticipant(organizerId, bookingUuid);
  const restoreParticipant = useRestoreParticipant(organizerId, bookingUuid);

  const isDeleted = Boolean(participant.deleted_at) || participant.status === 'removed';

  const handleDelete = async () => {
    if (!participant.id) return;
    if (!window.confirm(`Are you sure you want to remove ${participant.name}?`)) return;

    try {
      await deleteParticipant.mutateAsync(String(participant.id));
      onSuccess();
    } catch (err) {
      onError('Failed to remove participant');
    }
  };

  const handleRestore = async () => {
    if (!participant.id) return;

    try {
      await restoreParticipant.mutateAsync(String(participant.id));
      onSuccess();
    } catch (err) {
      onError('Failed to restore participant');
    }
  };

  return (
    <div className={`participant-inline ${isDeleted ? 'deleted' : ''}`}>
      <div className="participant-inline-info">
        <span className="participant-inline-name">{participant.name}</span>
        {participant.birthdate && (
          <span className="participant-inline-birthdate">
            ({new Date(participant.birthdate).toLocaleDateString()})
          </span>
        )}
        {participant.details && (
          <span className="participant-inline-details">- {participant.details}</span>
        )}
        {isDeleted && <span className="participant-deleted-badge">Removed</span>}
      </div>

      {isDeleted ? (
        <button
          className="btn-icon btn-icon--restore"
          onClick={handleRestore}
          disabled={restoreParticipant.isPending}
          title="Restore participant"
        >
          <RotateCcw className="icon icon--xs" />
        </button>
      ) : (
        <button
          className="btn-icon btn-icon--delete"
          onClick={handleDelete}
          disabled={deleteParticipant.isPending}
          title="Remove participant"
        >
          <Trash2 className="icon icon--xs" />
        </button>
      )}
    </div>
  );
};

export default OrganizerAdminCampBookings;
